import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IL_COLORS } from "../../../../constants/investmentLeague";

const AlertIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StatDivider = ({ color }) => (
  <Box sx={{ width: "1px", height: "40px", background: `${color}33`, flexShrink: 0 }} />
);

const EligibilityBanner = ({ summary }) => {
  const theme = useTheme();
  const t     = theme.custom;

  if (!summary) return null;

  const { greenCount, redCount, totalInvestments, eligible, greenPct } = summary;
  const accentColor = eligible ? IL_COLORS.positive : IL_COLORS.negative;
  const bgColor     = eligible ? IL_COLORS.positiveBg : IL_COLORS.negativeBg;

  const stats = [
    { label: `${greenCount} GREEN`,      color: IL_COLORS.positive },
    { label: `${redCount} RED`,          color: IL_COLORS.negative  },
    { label: `${greenPct}% IN GREEN`,    color: t.text              },
  ];

  return (
    <Box
      sx={{
        background:    bgColor,
        border:        `1px solid ${accentColor}55`,
        borderRadius:  "10px",
        p:             { xs: 2, md: 3 },
        mb:            3,
        display:       "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems:    { xs: "flex-start", md: "center" },
        gap:           { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          width:          44,
          height:         44,
          borderRadius:   "50%",
          background:     accentColor,
          color:          "#fff",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          flexShrink:     0,
        }}
      >
        {eligible ? <CheckIcon /> : <AlertIcon />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize:   { xs: "18px", md: "22px" },
            fontWeight: 700,
            color:      accentColor,
            mb:         0.5,
          }}
        >
          {greenCount} / {totalInvestments} GREEN — {eligible ? "Eligible" : "Not Yet Eligible"}
        </Typography>
        <Typography sx={{ fontSize: "12.5px", color: t.text2, lineHeight: 1.6 }}>
          {eligible
            ? "All of your Base-round picks are green. You qualify for the jackpot at year end if this holds."
            : `${redCount} of your Base-round picks have fallen below their starting value. All ${totalInvestments} must be green at year end to win the jackpot.`}
        </Typography>
      </Box>

      <Box
        sx={{
          display:        "flex",
          alignItems:     "center",
          gap:            2,
          flexShrink:     0,
          alignSelf:      { xs: "stretch", md: "center" },
          justifyContent: { xs: "space-around", md: "flex-start" },
        }}
      >
        {stats.map((stat, i) => (
          <React.Fragment key={stat.label}>
            {i > 0 && <StatDivider color={accentColor} />}
            <Box sx={{ textAlign: "center", px: 1 }}>
              <Typography
                sx={{
                  fontSize:     "13px",
                  fontWeight:   700,
                  color:        stat.color,
                  whiteSpace:   "nowrap",
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default EligibilityBanner;
